import type { ApiResponsePagedResultContact } from "../../services/generated/waba-api/models/api-response-paged-result-contact";
import type { ContactSearchReq } from "../../services/generated/waba-api/models";
import { BSUID_PATTERN } from "./contact-identity.util";

const PHONE_SEARCH_PATTERN = /^\+?[\d\s().-]+$/;
const PHONE_TOKEN_PATTERN = /\+?\d[\d\s().-]{2,}\d/g;

/**
 * Strips separators and the leading "+" so "+212 762" matches a number stored
 * either as "+212762…" or "212762…" (the filter is a "contains" match).
 */
function normalizePhone(value: string): string {
  return value.replace(/[\s().-]/g, "").replace(/^\+/, "");
}

function extractPhoneToken(term: string): string | null {
  const matches = term.match(PHONE_TOKEN_PATTERN) || [];
  return (
    matches
      .map((match) => ({
        raw: match.trim(),
        normalized: normalizePhone(match.trim()),
      }))
      .filter((match) => match.normalized.length >= 3)
      .sort((a, b) => b.normalized.length - a.normalized.length)[0]?.raw ||
    null
  );
}

export function applyContactQuickSearch(
  filter: ContactSearchReq,
  rawTerm: string | null | undefined,
): ContactSearchReq {
  const nextFilter = { ...filter };
  delete nextFilter.name;
  delete nextFilter.phoneNumber;
  delete nextFilter.waUsername;
  delete nextFilter.bsuid;
  delete nextFilter.identityQuery;

  const term = rawTerm?.trim();
  if (!term) {
    return nextFilter;
  }

  // A leading "@" is a WhatsApp handle. Send it as free text like the team inbox does:
  // the backend strips the "@" and matches part of the username, so "@moha" finds
  // "@mohamed_92". The exact `waUsername` filter only found full handles.
  if (term.startsWith("@") && term.length > 1) {
    nextFilter.identityQuery = term;
    return nextFilter;
  }

  // "MA.1586725942900797" / "US.ENT.118..." — a business-scoped user ID pasted from a
  // detail panel or a support thread.
  if (BSUID_PATTERN.test(term)) {
    nextFilter.bsuid = term;
    return nextFilter;
  }

  const normalizedPhone = normalizePhone(term);
  if (
    PHONE_SEARCH_PATTERN.test(term) &&
    normalizedPhone.length >= 3
  ) {
    nextFilter.phoneNumber = normalizedPhone;
    return nextFilter;
  }

  const phoneToken = extractPhoneToken(term);
  if (phoneToken) {
    const normalizedToken = normalizePhone(phoneToken);
    nextFilter.phoneNumber = normalizedToken;
    const nameTerm = term
      .replace(phoneToken, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (nameTerm) {
      nextFilter.name = nameTerm;
    }
    return nextFilter;
  }

  nextFilter.identityQuery = term;
  return nextFilter;
}

/**
 * Contact pickers keep their skeleton until a result arrives. A search that is never
 * answered (dropped connection, gateway stall) would otherwise leave it spinning forever.
 */
export const CONTACT_SEARCH_TIMEOUT_MS = 20_000;

/** Empty page shown when a picker search fails, so the skeleton gives way to the empty state. */
export function emptyContactSearchResult(
  page = 0,
  size = 10,
): ApiResponsePagedResultContact {
  return {
    body: { entities: [], totalElements: 0, totalPages: 0, page, size, last: true },
  };
}
